import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { LangSrcService } from '../../services/lang-src/lang-src.service';
import { LangTgtService } from '../../services/lang-tgt/lang-tgt.service';
import { AuthService } from '../../services/auth/auth.service';
import { RedirectService } from '../../services/redirect/redirect.service';
import { WEFIT_PAGES, WefitSsoService, isWefitFeatureAvailable } from '../../services/sso/wefit-sso.service';
import { ProviderLogoPipe } from '../../pipes/provider-logo.pipe';
import { WefitPartnerService, WefitTrainingProgram } from '../../services/wefit/wefit-partner.service';
import { WefitNavigationService } from '../../services/wefit/wefit-navigation.service';
import { LangTgt } from '../../interfaces/lang-tgt';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AccessibilityModalComponent } from '../accessibility-modal/accessibility-modal.component';
import { TranslateModule } from '@ngx-translate/core';

const WEFIT_GROUP_LOGO_BASE = 'https://lms2.wefitgroup.com/assets/img_group/';

// Codes langue Wefit → codes GS quand ils diffèrent (ex. coréen : kr chez Wefit, ko chez nous)
const WEFIT_LANG_CODE_MAP: Record<string, string> = {
  kr: 'ko',
};

// Marqueur d'extraction ngx-translate (npm run translate)
const _ = (s: string) => s;

// Libellés langue façon Wefit : article + minuscule (« l'anglais », « le français »)
const LANG_ARTICLE_LABELS: Record<string, string> = {
  af: _('l\'afrikaans'),
  ar: _('l\'arabe'),
  de: _('l\'allemand'),
  el: _('le grec'),
  en: _('l\'anglais'),
  es: _('l\'espagnol'),
  fr: _('le français'),
  it: _('l\'italien'),
  ja: _('le japonais'),
  ko: _('le coréen'),
  ru: _('le russe'),
  zh: _('le chinois'),
};

// Niveau CECRL (A1…C2) présent dans les intitulés des TP Wefit
const LEVEL_REGEX = /\b([ABC][12])\b/i;

@Component({
  standalone: true,
  selector: 'app-sidebar',
  imports: [CommonModule, RouterModule, ProviderLogoPipe, TranslateModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {

  loading = true;
  user: any;
  isDys = false;
  isBigFont = false;
  providerId: number | null = null;
  groupLogoUrl: string | null = null;
  readonly wefitPages = WEFIT_PAGES;
  languages: LangTgt[] = [];
  sidebarOpen = false;
  isLms2 = false;
  langFormationMap: Record<string, number> = {};
  langLevelMap: Record<string, string> = {};
  langDropdownOpen = false;

  @ViewChild('langDropdown') langDropdownRef: ElementRef<HTMLElement>;

  private signInSub: Subscription;

  constructor(
    public langSrcSrv: LangSrcService,
    public langTgtSrv: LangTgtService,
    private authService: AuthService,
    private redirectSrv: RedirectService,
    private wefitNavSrv: WefitNavigationService,
    private wefitPartnerSrv: WefitPartnerService,
    private wefitSsoSrv: WefitSsoService,
    private modal: NgbModal
  ) {}

  async ngOnInit() {
    const isLogged = await this.authService.isReallyLogged();
    if (isLogged) {
      await this.setLanguages();
    }
    this.user = await this.authService.getUser();
    this.isDys = this.user?.isDys ?? false;
    this.isBigFont = this.user?.isBigFont ?? false;
    [this.providerId, this.isLms2] = await Promise.all([
      this.wefitSsoSrv.getProviderId(),
      this.wefitSsoSrv.isLms2(),
    ]);
    this.loading = false;
    this.resolveGroupLogo();

    this.signInSub = this.authService.signInEvent.subscribe(async () => {
      await this.setLanguages();
      this.user = await this.authService.getUser();
      this.resolveGroupLogo();
    });
  }

  ngOnDestroy() {
    this.signInSub?.unsubscribe();
  }

  async setLanguages() {
    const [allLangs, tps]: [LangTgt[], WefitTrainingProgram[] | null] = await Promise.all([
      this.langTgtSrv.findAll() as Promise<LangTgt[]>,
      this.wefitPartnerSrv.getTrainingPrograms(),
    ]);
    const activeLangs = allLangs.filter(l => l.is_active);

    if (tps !== null) {
      const toGsCode = (formationCode: string) => {
        const code = formationCode.toLowerCase();
        return WEFIT_LANG_CODE_MAP[code] ?? code;
      };
      const codes = [...new Set(tps.map(tp => toGsCode(tp.formation_code)))];
      this.languages = activeLangs.filter(l => codes.includes(l.code));

      // Map langCode → formation_id pour la redirection LMS2
      // + langCode → niveau CECRL pour le badge du dropdown
      this.langFormationMap = {};
      this.langLevelMap = {};
      for (const tp of tps) {
        const code = toGsCode(tp.formation_code);
        if (!this.langFormationMap[code]) {
          this.langFormationMap[code] = tp.formation_id;
        }
        if (!this.langLevelMap[code]) {
          const level = `${tp.tp_name || ''} ${tp.formation || ''} ${tp.elearning_name || ''}`.match(LEVEL_REGEX);
          if (level) {
            this.langLevelMap[code] = level[1].toUpperCase();
          }
        }
      }
    } else {
      // Fallback si l'API Wefit est indisponible
      const fallbackCode = this.langSrcSrv.current?.shortCode === 'en' ? 'fr' : 'en';
      this.languages = activeLangs.filter(l => l.code === fallbackCode);
    }
  }

  trackById(index: number, item: any) { return item.id; }

  // Libellé affiché dans le dropdown : « l'anglais » plutôt que « Anglais »
  langLabel(lang: LangTgt): string {
    return LANG_ARTICLE_LABELS[lang.code] ?? lang.name;
  }

  // Langue cible affichée dans le bouton du dropdown
  get currentLang(): LangTgt | null {
    const code = this.langTgtSrv.current?.code;
    if (!code) {
      return null;
    }
    return this.languages.find(l => l.code === code) ?? this.langTgtSrv.current;
  }

  toggleLangDropdown() {
    this.langDropdownOpen = !this.langDropdownOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.langDropdownOpen && !this.langDropdownRef?.nativeElement.contains(event.target as Node)) {
      this.langDropdownOpen = false;
    }
  }

  // Logo du groupe : affiché uniquement si le fichier png ou jpg existe sur le LMS2
  private async resolveGroupLogo() {
    this.groupLogoUrl = null;
    const groupId = this.user?.group?.id ?? this.user?.group_id ?? this.user?.groupId ?? null;
    if (!groupId) {
      return;
    }
    for (const ext of ['png', 'jpg']) {
      const url = `${WEFIT_GROUP_LOGO_BASE}${groupId}.${ext}`;
      const exists = await this.imageExists(url);
      if (exists) {
        this.groupLogoUrl = url;
        return;
      }
    }
  }

  private imageExists(url: string): Promise<boolean> {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  }

  isFeatureAvailable(feature: string): boolean {
    return isWefitFeatureAvailable(feature, this.langTgtSrv.current?.code);
  }

  goToWefitPage(path: string = '') {
    this.wefitNavSrv.goToPage(path);
  }

  selectLanguage(lang: LangTgt) {
    this.langDropdownOpen = false;
    if (this.isLms2) {
      const formationId = this.langFormationMap[lang.code];
      if (formationId) {
        this.wefitNavSrv.goToFormation(formationId);
        return;
      }
    }
    this.redirectSrv.playSubject.next({ language: lang });
    this.closeSidebar();
  }

  toggleSidebar() {
    this.sidebarOpen = !this.sidebarOpen;
  }

  closeSidebar() {
    this.sidebarOpen = false;
  }

  openAccessibility() {
    const ref = this.modal.open(AccessibilityModalComponent, { centered: true, size: 'sm' });
    ref.componentInstance.isDys = this.isDys;
    ref.componentInstance.isBigFont = this.isBigFont;
    ref.dismissed.subscribe(() => {
      this.isDys = document.body.classList.contains('dys');
      this.isBigFont = document.body.classList.contains('big-font');
    });
  }
}
